import { t } from "@lingui/macro";
import { ArrowUp, Microphone, MicrophoneSlash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, ScrollArea } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import axios from "axios";
import { useEffect, useRef, useState } from "react";

import { FancyLoader } from "@/client/components/fancy-loader";
import { ScoreLoader } from "@/client/components/score-loader";
import { ScorePercentage } from "@/client/components/score-percentage";
import { useAudioRecorder } from "@/client/hooks/useAudioRecorder";
import { useAudioChatStore } from "@/client/stores/audioChatStore";

import { useResumeIntakeTranscriptStore } from "./_stores/resumeIntakeTranscriptStore";
import { EducationChat } from "./educationChat";
import { GenerateResumeChat } from "./generateResumeChat";
import { JdChat } from "./jdChat";
import { PersonalInfoChat } from "./personalnfoChat";
import { SiriWave } from "./siriWave";
import { SkillChat } from "./skillChat";
import { TemplateSelection } from "./templateSelection";
import { WorkExpChat } from "./workExpChat";

interface AudioChatDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const maxHeight = "10rem";

export const AudioChatDialog: React.FC<AudioChatDialogProps> = ({ isOpen, onClose }) => {
  const isLoading = useAudioChatStore((state) => state.isLoading);
  const progressBar = useAudioChatStore((state) => state.progressBar);
  const progressBarDesc = useAudioChatStore((state) => state.progressBarDesc);
  const setIsLoading = useAudioChatStore((state) => state.setIsLoading);
  const setChatTextData = useAudioChatStore((state) => state.setChatTextData);
  const clearTranscriptEvents = useResumeIntakeTranscriptStore((state) => state.clearEvents);
  const { isRecording, startRecording, stopRecording } = useAudioRecorder(handleAudioStop);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [inputText, setInputText] = useState("");
  const [showAudioFeature, setShowAudioFeature] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [setChatTextData, isLoading]);

  useEffect(() => {
    if (!isOpen) clearTranscriptEvents();
  }, [clearTranscriptEvents, isOpen]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeightPx = parseInt(maxHeight) * 16;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeightPx)}px`;
    }
  }, [inputText, maxHeight]);

  useEffect(() => {
    focusTextarea();
  }, [isLoading]);

  useEffect(() => {
    if (showAudioFeature)
      setTimeout(() => {
        setShowAudioFeature(false);
      }, 3000);
  }, [showAudioFeature]);

  const focusTextarea = () => {
    if (textareaRef.current) textareaRef.current.focus({ preventScroll: true });
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      const viewport = scrollAreaRef.current?.querySelector<HTMLDivElement>(
        "[data-radix-scroll-area-viewport]",
      );

      if (viewport) {
        viewport.scrollTo({ top: viewport.scrollHeight, behavior: "smooth" });
        return;
      }

      if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const updateInputText = (input: string) => {
    if (inputText) {
      setInputText(`${inputText}\n${input}`);
    } else {
      setInputText(input);
    }
  };

  const handleSendMessage = (input: string = inputText) => {
    if (input.trim()) {
      setChatTextData(input);
      setInputText("");
      scrollToBottom();
    }
  };

  const closeDialogue = () => {
    onClose();
  };

  async function handleAudioStop(audioBlob: Blob) {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "audio.wav");
      const response = await axios.post("/api/audio-to-text", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcription = response.data.text;
      updateInputText(transcription);
    } catch (error) {
      console.error(t`Error processing audio:`, error);
      // TODO: handle error gracefully
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex h-[99vh] min-h-0 w-full flex-col gap-1 sm:max-w-[960px]"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="mb-2 text-center text-xl font-bold">
            <div className="flex justify-center">
              <div className="flex gap-2">{t`Create Your Resume with AI Assistance`}</div>
            </div>
            <div className="grid grid-cols-12 items-center text-base">
              <div className="col-span-3 text-primary">{progressBarDesc}</div>
              <div className="col-span-7">
                <ScoreLoader score={progressBar} scoreKey="chatProgress" thickness="1.5" />
              </div>
              <div className="col-span-2 text-primary">
                <div className="flex justify-center gap-2">
                  <ScorePercentage score={progressBar} size="small" />
                  {t`Completed`}
                </div>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea ref={scrollAreaRef} className="relative min-h-0 grow pr-4">
          <JdChat focusTextarea={focusTextarea} />
          <PersonalInfoChat />
          <WorkExpChat focusTextarea={focusTextarea} />
          <SkillChat />
          <EducationChat focusTextarea={focusTextarea} scrollToBottom={scrollToBottom} />
          <TemplateSelection />

          <GenerateResumeChat
            closeDialogue={closeDialogue}
            scrollToBottom={scrollToBottom}
            focusTextarea={focusTextarea}
          />

          {isLoading && (
            <div className="flex justify-center">
              <FancyLoader />
            </div>
          )}

          <div className="h-32" />
          <div className="absolute bottom-0 grid w-full justify-center">
            <SiriWave width={480} height={128} amplitude={3} frequency={4} showWave={isRecording} />
          </div>
          <div ref={messagesEndRef} className="h-2" />
        </ScrollArea>

        <div className="mt-4 flex flex-col items-center space-y-4">
          <div className="flex w-full items-end space-x-3">
            <div className="group relative">
              <button
                className={cn(
                  "rounded-full border-2 border-gray-300 p-2 transition-all duration-500 hover:scale-105 focus:outline-none",
                  isLoading && "cursor-not-allowed",
                  isRecording && "border-primary",
                )}
                onClick={isRecording ? stopRecording : startRecording}
                onMouseOver={() => setShowAudioFeature(false)}
                disabled={isLoading}
              >
                {isRecording ? (
                  <Microphone size={32} weight="fill" className="text-primary" />
                ) : (
                  <MicrophoneSlash size={32} weight="fill" className="text-gray-300" />
                )}
              </button>
              <span
                className={cn(
                  "genai-gradient-2 dark:genai-gradient-4 absolute -top-14 left-0 z-20 origin-left whitespace-nowrap rounded-r-xl rounded-tl-xl px-3 py-2 text-sm font-medium text-primary transition-all duration-300 ease-in-out dark:text-black",
                  showAudioFeature ? "scale-100" : "scale-0",
                )}
              >
                {t`Try our audio feature`}
              </span>
              <span className="genai-gradient-2 dark:genai-gradient-4 absolute -top-14 left-0 z-20 origin-left scale-0 whitespace-nowrap rounded-r-xl rounded-tl-xl px-3 py-2 text-sm font-medium text-primary transition-all duration-300 ease-in-out group-hover:scale-100 dark:text-black">
                {isRecording ? t`Stop Recording` : t`Start Recording`}
              </span>
            </div>

            <textarea
              ref={textareaRef}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder={t`Type your message here...`}
              className="grow resize-none overflow-y-auto rounded-md border p-3 text-base focus:border-transparent focus:ring-2 focus:ring-primary dark:text-gray-800"
              disabled={isLoading || isRecording}
              rows={1}
              style={{ maxHeight }}
            />

            <button
              className={cn(
                "group rounded-full border-2 border-primary p-2 transition-all duration-500 hover:scale-105",
                (isLoading || isRecording || !inputText.trim()) && "border-secondary",
              )}
              onClick={() => handleSendMessage()}
            >
              <ArrowUp
                size={32}
                weight="bold"
                className={cn(
                  "text-primary transition-all duration-500 group-hover:-translate-y-2",
                  (isLoading || isRecording || !inputText.trim()) && "text-gray-300",
                )}
              />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
